import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { useBreakpoint } from '../../hooks/useMediaQuery';
import { cx } from '../../utils/cx';
import { Button } from '../Button/Button';
import { IconButton } from '../IconButton/IconButton';
import s from './CalendarGrid.module.css';

export type DayInfo = {
  date: string;
  inMonth: boolean;
  isToday: boolean;
  isPast: boolean;
  compact: boolean;
};

export type CalendarGridProps = {
  /** 'YYYY-MM' */
  month: string;
  /** 'YYYY-MM-DD' in the family's timezone */
  today: string;
  onMonthChange: (month: string) => void;
  /** What to draw in a day cell (chips from md, dots when compact). */
  renderDay?: (info: DayInfo) => ReactNode;
  /** Tapping a day (e.g. show that day's occasions, or start a new occasion on desktop). */
  onDayClick?: (date: string) => void;
  /** Accessible description for a day, e.g. "2 occasions". */
  describeDay?: (date: string) => string | undefined;
  /** 0 = Sunday. */
  weekStartsOn?: 0 | 1;
  locale?: string;
  className?: string;
};

const pad = (n: number) => String(n).padStart(2, '0');
const iso = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`;

export function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}`;
}

/** Weeks of dates covering the month (with leading/trailing days from neighbours). */
export function monthMatrix(month: string, weekStartsOn: 0 | 1 = 0): string[][] {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const first = new Date(Date.UTC(y, m - 1, 1));
  const offset = (first.getUTCDay() - weekStartsOn + 7) % 7;
  const start = Date.UTC(y, m - 1, 1 - offset);
  const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const weeks = Math.ceil((offset + daysInMonth) / 7);
  return Array.from({ length: weeks }, (_, w) =>
    Array.from({ length: 7 }, (_, d) => {
      const dt = new Date(start + (w * 7 + d) * 86_400_000);
      return iso(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());
    }),
  );
}

/** Generic month grid: knows nothing about occasions. Compact dot mode on phones. */
export function CalendarGrid({
  month,
  today,
  onMonthChange,
  renderDay,
  onDayClick,
  describeDay,
  weekStartsOn = 0,
  locale,
  className,
}: CalendarGridProps) {
  const compact = !useBreakpoint('md');
  const weeks = monthMatrix(month, weekStartsOn);
  const [y, m] = month.split('-').map(Number) as [number, number];
  const title = new Intl.DateTimeFormat(locale, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(y, m - 1, 1)));
  const weekdays = (weeks[0] ?? []).map((d) =>
    new Intl.DateTimeFormat(locale, {
      weekday: compact ? 'narrow' : 'short',
      timeZone: 'UTC',
    }).format(new Date(`${d}T00:00:00Z`)),
  );
  const longDate = (d: string) =>
    new Intl.DateTimeFormat(locale, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(`${d}T00:00:00Z`));

  return (
    <div className={cx(s.calendar, className)}>
      <div className={s.head}>
        <h2 className={s.title} aria-live="polite">
          {title}
        </h2>
        <div className={s.nav}>
          <Button size="sm" variant="ghost" onClick={() => onMonthChange(today.slice(0, 7))}>
            Today
          </Button>
          <IconButton
            icon={ChevronLeft}
            label="Previous month"
            size="sm"
            onClick={() => onMonthChange(shiftMonth(month, -1))}
          />
          <IconButton
            icon={ChevronRight}
            label="Next month"
            size="sm"
            onClick={() => onMonthChange(shiftMonth(month, 1))}
          />
        </div>
      </div>
      <table className={s.grid} data-compact={compact || undefined}>
        <caption className={s.sr}>{title}</caption>
        <thead>
          <tr>
            {weekdays.map((w, i) => (
              <th key={i} scope="col" className={s.weekday}>
                {w}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week) => (
            <tr key={week[0]}>
              {week.map((date) => {
                const info: DayInfo = {
                  date,
                  inMonth: date.slice(0, 7) === month,
                  isToday: date === today,
                  isPast: date < today,
                  compact,
                };
                const extra = describeDay?.(date);
                const label = `${longDate(date)}${info.isToday ? ', today' : ''}${extra ? `, ${extra}` : ''}`;
                const dayNum = Number(date.slice(8));
                const content = (
                  <>
                    <span className={s.num}>{dayNum}</span>
                    {renderDay ? <span className={s.contents}>{renderDay(info)}</span> : null}
                  </>
                );
                return (
                  <td
                    key={date}
                    className={s.cell}
                    data-out={!info.inMonth || undefined}
                    data-today={info.isToday || undefined}
                    data-past={info.isPast || undefined}
                  >
                    {onDayClick ? (
                      <button
                        type="button"
                        className={s.day}
                        aria-label={label}
                        onClick={() => onDayClick(date)}
                      >
                        {content}
                      </button>
                    ) : (
                      <div className={s.day} aria-label={label}>
                        {content}
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
